import { useAuth } from '@clerk/clerk-react';
import { useMemo } from 'react';

const baseUrl = (import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3000').replace(/\/$/, '');

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;
  constructor(status: number, body: unknown) {
    // Two error-body shapes flow through this class:
    //   1. Our canonical shape: { error: { code, message, details } } — produced by
    //      Fastify routes that explicitly construct error replies.
    //   2. Fastify's default error shape: { statusCode, error: "Unauthorized",
    //      message: "..." } — produced by unhandled 4xx like 401 (requireRole) or
    //      404 (unknown route). Here `error` is a STRING, not an object.
    // Prior implementation tried to read `.message` off whichever `body.error` was
    // which silently produced an empty message when the string path was taken.
    const b = (body ?? {}) as {
      error?: { code?: string; message?: string; details?: unknown } | string;
      message?: string;
      statusCode?: number;
    };
    let code = `http_${status}`;
    let message = b.message ?? `request failed (${status})`;
    let details: unknown = b;
    if (b.error && typeof b.error === 'object') {
      code = b.error.code ?? code;
      message = b.error.message ?? message;
      details = b.error.details ?? b;
    } else if (typeof b.error === 'string') {
      // Fastify default error. Keep status + message; upgrade code to be informative.
      code = b.error.toLowerCase().replace(/\s+/g, '_');
    }
    super(`${message} [${code} · ${status}]`);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

// Thin fetch wrapper that attaches a Clerk bearer token to every request. Uses the
// signed-in session's short-lived token (Clerk refreshes automatically). Returns the
// parsed JSON body or throws `ApiError`.
export type ApiRequest = <T>(path: string, init?: RequestInit) => Promise<T>;

export function useApi(): ApiRequest {
  const { getToken } = useAuth();

  return useMemo<ApiRequest>(
    () =>
      async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
        const token = await getToken();
        const headers = new Headers(init.headers);
        if (token) headers.set('Authorization', `Bearer ${token}`);
        if (!headers.has('Content-Type') && init.body)
          headers.set('Content-Type', 'application/json');
        if (!headers.has('Accept')) headers.set('Accept', 'application/json');

        const res = await fetch(`${baseUrl}${path}`, { ...init, headers });
        const text = await res.text();
        const body = text ? (JSON.parse(text) as unknown) : undefined;

        if (!res.ok) throw new ApiError(res.status, body);
        return body as T;
      },
    [getToken],
  );
}

export const API_BASE_URL = baseUrl;
